import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getVodTmdbData, getSeriesTmdbData, clearEpisodeName } from "../other/tmdb";
import { downloadEpgDataFromCategories } from "../other/epg-database";
import { groupBy } from '../other/group-by';

// Thunks
export const loadGroup = createAsyncThunk(
  'media/loadGroup',
  async (mode) => {
    const url = `${window.player}&action=${getAddress(mode, true)}`;
    const response = await fetch(url);
    const data = await response.json();
    return data;
  }
);

export const loadPlaylist = createAsyncThunk(
  'media/loadPlaylist',
  async ({ mode, group }) => {
    const url = `${window.player}&action=${getAddress(mode, false)}&category_id=${isNaN(group) ? "*" : group}`;
    const response = await fetch(url);
    const result = await response.json();

    if (mode === "live" && !isNaN(group) && getIsIptveditor() === true) {
      await downloadEpgDataFromCategories(result);
    }

    console.log('playlist fetched', result);

    return result;
  }
);

export const loadEpg = createAsyncThunk(
    'media/loadEpg',
    async ({ epg_id, limit = 1 }) => {
      const now = new Date();
      const url = `${window.epg}&action=get_simple_data_table&epg_id=${epg_id}&limit=${limit}&start=${Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate() + limit - 1, 0, 0, 0, 0).getTime() / 1000)}&stop=${Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate() + limit - 1, 23, 59, 59, 999).getTime() / 1000)}`;
  
      const response = await fetch(url);
      const text = await response.text();
      const parser = new DOMParser();
      const data = parser.parseFromString(text, "application/xml");
    console.log('data fetched')
      console.log(data);
  
      // Cache the queried programs once and group by channel
      const programMap = new Map();
  
      Array.from(data.querySelectorAll('programme')).forEach(program => {
        const channelId = program.getAttribute('channel');
        const programData = {
          title: program.querySelector('title')?.textContent || 'Unknown',
          description: program.querySelector('desc')?.textContent || 'No description',
          start: program.getAttribute('start'),
          end: program.getAttribute('stop'),
        };
  
        if (!programMap.has(channelId)) {
          programMap.set(channelId, []);
        }
  
        programMap.get(channelId).push(programData);
      });
  
      // Now iterate over channels and link programs from the map
      const channels = Array.from(data.querySelectorAll('channel')).map(channel => {
        const channelId = channel.getAttribute('id');
        const channelName = channel.querySelector('display-name')?.textContent || 'Unknown';
  
        return {
          id: channelId,
          name: channelName,
          programs: programMap.get(channelId) || [],
        };
      });
  
      return channels; // Return the plain object to Redux
    }
  );
  
  
export const loadEpgArray = createAsyncThunk(
  'media/loadEpgArray',
  async ({ epg_ids, limit = 2 }) => {
    const now = new Date();
    const programmes = [];
    const set = new Set();
    const map = {};

    epg_ids.filter(x => !!x).forEach(x => {
      const id = x.replace(/\.(plus|minus)\d+$/, "");
      set.add(id);

      const shift = x.match(/\.(plus|minus)(?<number>\d+)$/);
      if (shift && shift.groups) {
        const list = map[id] || [];
        list.push(shift.groups.number);
        map[id] = list;
      }
    });
    
    epg_ids = Array.from(set);

    for (let i = 0, j = epg_ids.length; i < j; i += 50) {
      const sliced = epg_ids.slice(i, i + 50);
      const url = `${window.epg}&action=get_simple_data_table_array&epg_ids=${sliced.join(',')}&limit=${limit}&start=${Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate() + limit - 1, 0, 0, 0, 0).getTime() / 1000)}&stop=${Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate() + limit - 1, 23, 59, 59, 999).getTime() / 1000)}`;
      const response = await fetch(url);
      const text = await response.text();
      const parser = new DOMParser();
      const result = parser.parseFromString(text, "application/xml");
      Object.keys(map).filter(c => sliced.includes(c)).forEach(c => {
        const pgs = result.filter(x => x.id === c);
        map[c].forEach(shift => {
          const id = `${c}.${shift > 0 ? `plus${shift}` : `minus${shift}`}`;
          pgs.forEach(p => programmes.push({
            ...p,
            id,
            start: p.start + (shift * 3600),
            stop: p.stop + (shift * 3600)
          }));
        });
      });

      programmes.push(...result);
    }

    return groupBy(programmes, "id");
  }
);

export const getVodInfo = createAsyncThunk(
  'media/getVodInfo',
  async ({ vod_id, name, existingTmdb }) => {
    let result = null;
    if (getIsIptveditor() === false) {
      const url = `${window.player}&action=get_vod_info&vod_id=${vod_id}`;
      const response = await fetch(url);
      result = await response.json();
    }

    if (result && result.info && result.info.cover_big) {
      return result;
    }

    return await getVodTmdbData(name, existingTmdb);
  }
);

export const getSeriesInfo = createAsyncThunk(
  'media/getSeriesInfo',
  async ({ series_id, name, only_info, existingTmdb }) => {
    let result = null;
    if (getIsIptveditor() === false || only_info === false) {
      const url = `${window.player}&action=get_series_info&series_id=${series_id}`;
      const response = await fetch(url);
      result = await response.json();
    }

    if (getIsIptveditor() === true) {
      return await getSeriesTmdbData(name, result, existingTmdb);
    } else if (only_info === false) {
      clearEpisodeName(name, result);
    }
    return result;
  }
);

const mediaSlice = createSlice({
  name: 'media',
  initialState: {
    groupData: [],
    playlistData: [],
    epgData: [],
    epgArrayData: [],
    vodInfo: {},
    seriesInfo: {},
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadGroup.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadGroup.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.groupData = action.payload;
      })
      .addCase(loadGroup.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(loadPlaylist.fulfilled, (state, action) => {
        state.playlistData = action.payload;
      })
      .addCase(loadEpg.fulfilled, (state, action) => {
        state.epgData = action.payload;
      })
      .addCase(loadEpgArray.fulfilled, (state, action) => {
        state.epgArrayData = action.payload;
      })
      .addCase(getVodInfo.fulfilled, (state, action) => {
        state.vodInfo = action.payload;
      })
      .addCase(getSeriesInfo.fulfilled, (state, action) => {
        state.seriesInfo = action.payload;
      });
  },
});

const getAddress = (mode, group) => {
  switch (mode) {
    case "live":
      return group ? "get_live_categories" : "get_live_streams";
    case "movie":
      return group ? "get_vod_categories" : "get_vod_streams";
    case "series":
      return group ? "get_series_categories" : "get_series";
    default:
      return "";
  }
};

const getIsIptveditor = () => {
  return false;
};

export default mediaSlice.reducer;
